import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExportIcon from '@mui/icons-material/FileDownloadOutlined';
import CourseIcon from '@mui/icons-material/DirectionsRun';
import VeloIcon from '@mui/icons-material/DirectionsBike';
import NageIcon from '@mui/icons-material/Pool';
import AutreIcon from '@mui/icons-material/FitnessCenter';
import RealiseeIcon from '@mui/icons-material/CheckCircle';
import ManqueeIcon from '@mui/icons-material/EventBusy';
import ReposIcon from '@mui/icons-material/HotelOutlined';
import NoteIcon from '@mui/icons-material/StickyNote2Outlined';
import TrophyIcon from '@mui/icons-material/EmojiEventsOutlined';
import AttentionIcon from '@mui/icons-material/WarningAmber';
import { Link } from 'react-router-dom';

import { formaterDuree, formaterDureeCourse } from '../Construction/formatDuree.ts';
import { estNoteJournee, infoSemaine, type SeancePlanifiee } from '../Planning/planningApi.ts';
import { formaterKm, iso, JOURS, joursAffiches } from '../Planning/dates.ts';
import { lundiDe } from '../Planning/decompte.ts';
import {
  allureMoyenne,
  estCourse,
  exporterJournal,
  lireJournal,
  type ActiviteRealisee,
  type Journal,
} from './journalApi.ts';

type Statut = 'realisee' | 'manquee' | 'aFaire' | 'prevue';

function IconeSport({ a, taille }: { a: ActiviteRealisee; taille: number }) {
  const sx = { fontSize: taille };
  if (estCourse(a)) return <CourseIcon sx={sx} />;
  if (a.type && /Ride/.test(a.type)) return <VeloIcon sx={sx} />;
  if (a.type && /Swim/.test(a.type)) return <NageIcon sx={sx} />;
  return <AutreIcon sx={sx} />;
}

function dureePrevue(p: SeancePlanifiee): string {
  if (!p.dureeTotaleS) return '';
  return p.categorie === 'COMPETITION' ? formaterDureeCourse(p.dureeTotaleS) : formaterDuree(p.dureeTotaleS);
}

const LIBELLES_STATUT: Record<Statut, string> = {
  realisee: 'Réalisée',
  manquee: 'Non réalisée',
  aFaire: "Prévue aujourd'hui",
  prevue: 'Prévue',
};

/**
 * Journal d'entrainement : sur le modele du calendrier, les activites
 * realisees (lues dans intervals.icu, ou la montre les depose, et conservees
 * par le serveur) jour par jour,
 * rapprochees des seances prevues ; les seances prevues non realisees
 * restent visibles. Totaux realises / prevus par semaine et pour le mois.
 */
export default function JournalEntrainement() {
  const [mois, setMois] = React.useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [journal, setJournal] = React.useState<Journal | null>(null);
  const [chargement, setChargement] = React.useState(false);
  const [erreur, setErreur] = React.useState<string | null>(null);
  // Export Google Sheets : periode choisie (null : fenetre fermee).
  const [export_, setExport] = React.useState<{ debut: string; fin: string } | null>(null);
  const [exportEnCours, setExportEnCours] = React.useState(false);
  const [erreurExport, setErreurExport] = React.useState<string | null>(null);

  const jours = React.useMemo<Date[]>(() => joursAffiches(mois), [mois]);
  const debut = iso(jours[0]);
  const fin = iso(jours[jours.length - 1]);
  const aujourdhui = iso(new Date());
  const lundiCourant = iso(lundiDe(new Date()));

  const charger = React.useCallback((actualiser = false) => {
    setChargement(true);
    setErreur(null);
    lireJournal(debut, fin, actualiser)
      .then(setJournal)
      .catch((e: Error) => setErreur('Journal indisponible : ' + e.message))
      .finally(() => setChargement(false));
  }, [debut, fin]);

  React.useEffect(() => {
    charger();
  }, [charger]);

  const planifiees = React.useMemo<SeancePlanifiee[]>(() => journal?.planifiees ?? [], [journal]);
  const activites = React.useMemo<ActiviteRealisee[]>(() => journal?.activites ?? [], [journal]);

  // Seance prevue -> activite qui la realise.
  const realisation = React.useMemo(() => {
    const m = new Map<string, ActiviteRealisee>();
    activites.forEach((a) => {
      if (a.idPlanifiee) m.set(a.idPlanifiee, a);
    });
    return m;
  }, [activites]);
  const planifieeParId = React.useMemo(
    () => new Map<string, SeancePlanifiee>(planifiees.map((p): [string, SeancePlanifiee] => [p.id, p])),
    [planifiees],
  );

  const statutDe = React.useCallback(
    (p: SeancePlanifiee): Statut => {
      if (realisation.has(p.id)) return 'realisee';
      if (p.date < aujourdhui) return 'manquee';
      return p.date === aujourdhui ? 'aFaire' : 'prevue';
    },
    [realisation, aujourdhui],
  );

  const activitesParJour = React.useMemo(() => {
    const m = new Map<string, ActiviteRealisee[]>();
    activites.forEach((a) => m.set(a.date, [...(m.get(a.date) ?? []), a]));
    return m;
  }, [activites]);

  // Seances prevues affichees a part : celles qui ne sont pas (encore) realisees.
  const prevuesParJour = React.useMemo(() => {
    const m = new Map<string, SeancePlanifiee[]>();
    planifiees
      .filter((p) => !realisation.has(p.id))
      .sort((a, b) => a.ordre - b.ordre)
      .forEach((p) => m.set(p.date, [...(m.get(p.date) ?? []), p]));
    return m;
  }, [planifiees, realisation]);

  // Totaux realises et prevus sur un ensemble de jours.
  const totaux = React.useCallback(
    (dates: Set<string>) => {
      const faites = activites.filter((a) => dates.has(a.date));
      const prevues = planifiees.filter((p) => dates.has(p.date) && !estNoteJournee(p.categorie));
      const echues = prevues.filter((p) => p.date <= aujourdhui);
      return {
        nbActivites: faites.length,
        distance: faites.reduce((t, a) => t + (a.distanceM ?? 0), 0),
        duree: faites.reduce((t, a) => t + (a.dureeS ?? 0), 0),
        distancePrevue: prevues.reduce((t, p) => t + (p.distanceTotaleM ?? 0), 0),
        dureePrevue: prevues.reduce((t, p) => t + (p.dureeTotaleS ?? 0), 0),
        chargePrevue: prevues.reduce((t, p) => t + Math.round(p.chargeUA ?? 0), 0),
        nbEchues: echues.length,
        nbRealisees: echues.filter((p) => realisation.has(p.id)).length,
      };
    },
    [activites, planifiees, realisation, aujourdhui],
  );

  const totalMois = React.useMemo(
    () => totaux(new Set(jours.filter((j) => j.getMonth() === mois.getMonth()).map(iso))),
    [totaux, jours, mois],
  );

  const exporter = () => {
    if (!export_) return;
    setExportEnCours(true);
    setErreurExport(null);
    exporterJournal(export_.debut, export_.fin)
      .then(() => setExport(null))
      .catch((e: Error) => setErreurExport('Export impossible : ' + e.message))
      .finally(() => setExportEnCours(false));
  };

  const titreMois = mois.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const incompletes = activites.some((a) => a.incomplete);

  const carteActivite = (a: ActiviteRealisee, horsMois: boolean) => {
    const prevue = a.idPlanifiee ? planifieeParId.get(a.idPlanifiee) : undefined;
    const mesures = [formaterKm(a.distanceM), a.dureeS ? formaterDuree(a.dureeS) : ''].filter(Boolean).join(' · ');
    const allure = estCourse(a) ? allureMoyenne(a) : '';
    return (
      <Tooltip
        key={a.id}
        arrow
        title={
          <Box>
            <b>{a.nom}</b>
            <Box>{[a.heure, a.type].filter(Boolean).join(' · ')}</Box>
            {a.incomplete ? (
              <Box sx={{ mt: 0.5 }}>Activité importée de Strava : intervals.icu n'en transmet pas le détail.</Box>
            ) : (
              <>
                <Box>
                  {[formaterKm(a.distanceM), a.dureeS ? `${formaterDuree(a.dureeS)} en mouvement` : '', allure]
                    .filter(Boolean).join(' · ')}
                </Box>
                {(a.deniveleM ?? 0) >= 1 && <Box>D+ {Math.round(a.deniveleM ?? 0)} m</Box>}
                {a.fcMoyenne && <Box>FC moy. {a.fcMoyenne}{a.fcMax ? ` · max ${a.fcMax}` : ''} bpm</Box>}
                {a.rpe ? <Box>RPE {a.rpe}</Box> : null}
              </>
            )}
            {prevue && (
              <Box sx={{ mt: 0.5 }}>
                Réalise « {prevue.nom} »
                {[dureePrevue(prevue), formaterKm(prevue.distanceTotaleM)].filter(Boolean).length > 0 &&
                  ` (prévu : ${[dureePrevue(prevue), formaterKm(prevue.distanceTotaleM)].filter(Boolean).join(' · ')})`}
                {a.appariement === 'JOUR' && <Box sx={{ opacity: 0.85 }}>Rapprochée par le jour</Box>}
              </Box>
            )}
            <Box sx={{ mt: 0.5, opacity: 0.85 }}>Clic : ouvrir dans intervals.icu</Box>
          </Box>
        }
      >
        <Box
          component="a"
          href={a.lien}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'block',
            textDecoration: 'none',
            color: 'text.primary',
            fontSize: '0.75rem',
            lineHeight: 1.25,
            p: '3px 6px',
            borderRadius: 0.75,
            bgcolor: prevue ? 'rgba(46, 125, 50, 0.10)' : 'rgba(25, 118, 210, 0.08)',
            borderLeft: 3,
            borderColor: prevue ? 'success.main' : 'primary.main',
            opacity: horsMois ? 0.6 : 1,
            '&:hover': { bgcolor: prevue ? 'rgba(46, 125, 50, 0.18)' : 'rgba(25, 118, 210, 0.14)' },
          }}
        >
          <Box sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <IconeSport a={a} taille={14} />
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
              {a.nom}
            </Box>
            {prevue && <RealiseeIcon color="success" sx={{ fontSize: 13 }} />}
            {a.incomplete && <AttentionIcon color="warning" sx={{ fontSize: 13 }} />}
          </Box>
          {mesures && <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{mesures}</Box>}
          {(allure || a.fcMoyenne) && (
            <Box sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
              {[allure, a.fcMoyenne ? `${a.fcMoyenne} bpm` : ''].filter(Boolean).join(' · ')}
            </Box>
          )}
        </Box>
      </Tooltip>
    );
  };

  const cartePrevue = (p: SeancePlanifiee, horsMois: boolean) => {
    if (p.categorie === 'NOTE') {
      return (
        <Tooltip key={p.id} arrow title={<Box sx={{ whiteSpace: 'pre-line' }}>{p.notes}</Box>}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem', color: 'text.secondary', px: 0.5 }}>
            <NoteIcon sx={{ fontSize: 13 }} /> {p.nom}
          </Box>
        </Tooltip>
      );
    }
    if (p.categorie === 'REPOS') {
      return (
        <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem', color: 'text.disabled', px: 0.5 }}>
          <ReposIcon sx={{ fontSize: 13 }} /> Repos
        </Box>
      );
    }
    const statut = statutDe(p);
    const manquee = statut === 'manquee';
    return (
      <Tooltip
        key={p.id}
        arrow
        title={
          <Box>
            <b>{p.nom}</b>
            <Box>{LIBELLES_STATUT[statut]}{p.categorie === 'COMPETITION' ? ' · compétition' : ''}</Box>
            <Box>{[p.zonePrincipale, dureePrevue(p), formaterKm(p.distanceTotaleM)].filter(Boolean).join(' · ')}</Box>
            {manquee && (
              <Box sx={{ mt: 0.5, opacity: 0.85 }}>
                Aucune activité de course ce jour-là dans intervals.icu.
              </Box>
            )}
          </Box>
        }
      >
        <Box
          sx={{
            fontSize: '0.75rem',
            lineHeight: 1.25,
            p: '2px 6px',
            borderRadius: 0.75,
            border: '1px dashed',
            borderColor: manquee ? 'error.light' : 'grey.400',
            color: manquee ? 'error.main' : 'text.secondary',
            opacity: horsMois ? 0.6 : 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            {manquee ? <ManqueeIcon sx={{ fontSize: 13 }} /> : p.categorie === 'COMPETITION' ? <TrophyIcon sx={{ fontSize: 13 }} /> : null}
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: manquee ? 'line-through' : 'none' }}>
              {p.nom}
            </Box>
          </Box>
          <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {[dureePrevue(p), formaterKm(p.distanceTotaleM)].filter(Boolean).join(' · ') || LIBELLES_STATUT[statut]}
          </Box>
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box component="main" sx={{ p: { xs: 2, md: 12 }, pt: { xs: 9, md: 10 } }}>

      {journal && !journal.compteConfigure && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Les séances réalisées sont lues dans intervals.icu : renseigne ton compte dans{' '}
          <Link to="/preferences?onglet=intervals">Mes préférences</Link> pour les voir ici
          {activites.length > 0 ? ' (activités déjà enregistrées affichées).' : '.'}
        </Alert>
      )}
      {incompletes && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Certaines activités viennent de Strava : intervals.icu n'en transmet pas le détail. Connecte ta montre
          (Garmin Connect…) directement à intervals.icu pour les retrouver complètes.
        </Alert>
      )}
      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}
      {journal?.avertissement && <Alert severity="warning" sx={{ mb: 2 }}>{journal.avertissement}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <IconButton aria-label="Mois précédent" onClick={() => setMois(new Date(mois.getFullYear(), mois.getMonth() - 1, 1))}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6" sx={{ minWidth: 190, textAlign: 'center', textTransform: 'capitalize' }}>
          {titreMois}
        </Typography>
        <IconButton aria-label="Mois suivant" onClick={() => setMois(new Date(mois.getFullYear(), mois.getMonth() + 1, 1))}>
          <ChevronRightIcon />
        </IconButton>
        <Button
          size="small"
          startIcon={<TodayIcon />}
          onClick={() => {
            const d = new Date();
            setMois(new Date(d.getFullYear(), d.getMonth(), 1));
          }}
        >
          Aujourd'hui
        </Button>
        <Tooltip title="Relire les activités de la période dans intervals.icu (sinon, seules les semaines récentes sont relues, au plus toutes les 10 minutes)">
          <span>
            <Button size="small" startIcon={<RefreshIcon />} onClick={() => charger(true)} disabled={chargement || !journal?.compteConfigure}>
              Actualiser
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Exporter le journal (une ligne par semaine) dans un fichier CSV à importer dans Google Sheets">
          <Button
            size="small"
            startIcon={<ExportIcon />}
            onClick={() => {
              setErreurExport(null);
              setExport({ debut, fin });
            }}
          >
            Exporter
          </Button>
        </Tooltip>
        {journal?.dateSynchro && (
          <Typography variant="caption" color="text.secondary">
            Lu dans intervals.icu le{' '}
            {new Date(journal.dateSynchro).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </Typography>
        )}
      </Box>

      {/* Bilan du mois */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, display: 'flex', gap: 4, flexWrap: 'wrap', fontVariantNumeric: 'tabular-nums' }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Réalisé</Typography>
          <Typography sx={{ fontWeight: 600 }}>
            {totalMois.nbActivites} activité{totalMois.nbActivites > 1 ? 's' : ''}
            {totalMois.distance > 0 && ` · ${formaterKm(totalMois.distance)}`}
            {totalMois.duree > 0 && ` · ${formaterDuree(totalMois.duree)}`}
          </Typography>
        </Box>
        {totalMois.chargePrevue > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary">Charge prévue</Typography>
            <Typography sx={{ fontWeight: 600 }}>{totalMois.chargePrevue} UA</Typography>
          </Box>
        )}
        <Box>
          <Typography variant="caption" color="text.secondary">Prévu sur le mois</Typography>
          <Typography sx={{ fontWeight: 600 }}>
            {[formaterKm(totalMois.distancePrevue), totalMois.dureePrevue ? formaterDuree(totalMois.dureePrevue) : ''].filter(Boolean).join(' · ') || '—'}
          </Typography>
        </Box>
        {totalMois.nbEchues > 0 && (
          <Box sx={{ minWidth: 180 }}>
            <Typography variant="caption" color="text.secondary">Séances prévues réalisées (à ce jour)</Typography>
            <Typography sx={{ fontWeight: 600 }}>
              {totalMois.nbRealisees} / {totalMois.nbEchues} · {Math.round((100 * totalMois.nbRealisees) / totalMois.nbEchues)} %
            </Typography>
            <LinearProgress
              variant="determinate"
              color="success"
              value={(100 * totalMois.nbRealisees) / totalMois.nbEchues}
              sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
            />
          </Box>
        )}
      </Paper>

      {chargement && <LinearProgress sx={{ mb: 0.5 }} />}

      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(110px, 1fr)) 160px', minWidth: 940, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          {[...JOURS, 'Semaine'].map((j) => (
            <Box key={j} sx={{ p: 1, bgcolor: 'action.hover', fontWeight: 600, fontSize: '0.8rem', textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
              {j}
            </Box>
          ))}

          {Array.from({ length: 6 }, (_, s) => jours.slice(s * 7, s * 7 + 7)).map((semaine, s) => {
            const total = totaux(new Set(semaine.map(iso)));
            const typeSemaine = infoSemaine(journal?.semaines?.find((x: { lundi: string; type: string | null }) => x.lundi === iso(semaine[0]))?.type);
            return (
              <React.Fragment key={s}>
                {semaine.map((jour) => {
                  const date = iso(jour);
                  // Les jours de la semaine en cours ne sont jamais grises.
                  const horsMois = jour.getMonth() !== mois.getMonth() && iso(lundiDe(jour)) !== lundiCourant;
                  return (
                    <Box
                      key={date}
                      sx={{
                        minHeight: 100,
                        p: 0.5,
                        borderRight: 1,
                        borderBottom: s < 5 ? 1 : 0,
                        borderColor: 'divider',
                        bgcolor: typeSemaine ? typeSemaine.fond : horsMois ? 'action.hover' : 'background.paper',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          alignSelf: 'flex-end',
                          fontWeight: date === aujourdhui ? 700 : 400,
                          color: horsMois ? 'text.disabled' : date === aujourdhui ? 'primary.contrastText' : 'text.secondary',
                          bgcolor: date === aujourdhui ? 'primary.main' : 'transparent',
                          borderRadius: '50%',
                          width: 22,
                          height: 22,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {jour.getDate()}
                      </Typography>
                      {(activitesParJour.get(date) ?? []).map((a) => carteActivite(a, horsMois))}
                      {(prevuesParJour.get(date) ?? []).map((p) => cartePrevue(p, horsMois))}
                    </Box>
                  );
                })}
                <Box
                  sx={{
                    p: 1,
                    borderBottom: s < 5 ? 1 : 0,
                    borderColor: 'divider',
                    bgcolor: typeSemaine ? typeSemaine.fond : 'action.hover',
                    borderLeft: typeSemaine ? `3px solid ${typeSemaine.couleur}` : 'none',
                    fontSize: '0.75rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                    justifyContent: 'center',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {typeSemaine && (
                    <Tooltip title={typeSemaine.description}>
                      <Box sx={{ alignSelf: 'flex-start', mb: 0.5, px: 0.75, borderRadius: 1, bgcolor: typeSemaine.couleur, color: '#fff', fontSize: '0.68rem', fontWeight: 600 }}>
                        {typeSemaine.libelle}
                      </Box>
                    </Tooltip>
                  )}
                  {/* Realise / prevu cote a cote : duree, km, charge (UA) */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 1, rowGap: 0.25, alignItems: 'baseline' }}>
                    <Box sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.3 }}>Réalisé</Box>
                    <Box sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.3 }}>Prévu</Box>

                    <Box sx={{ fontWeight: 600 }}>{total.duree > 0 ? formaterDuree(total.duree) : '—'}</Box>
                    <Box sx={{ color: 'text.secondary' }}>{total.dureePrevue > 0 ? formaterDuree(total.dureePrevue) : '—'}</Box>

                    <Box>{formaterKm(total.distance) || '—'}</Box>
                    <Box sx={{ color: 'text.secondary' }}>{formaterKm(total.distancePrevue) || '—'}</Box>

                    <Box />
                    <Box sx={{ color: 'text.secondary' }}>{total.chargePrevue > 0 ? `${total.chargePrevue} UA` : '—'}</Box>
                  </Box>
                  {total.nbEchues > 0 && (
                    <Box sx={{ color: total.nbRealisees === total.nbEchues ? 'success.main' : 'text.secondary', fontWeight: 600 }}>
                      {total.nbRealisees}/{total.nbEchues} réalisée{total.nbRealisees > 1 ? 's' : ''}
                    </Box>
                  )}
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      <Dialog open={!!export_} onClose={() => !exportEnCours && setExport(null)}>
        <DialogTitle>Exporter le journal</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Une ligne par semaine (du lundi au dimanche) : jour par jour le kilométrage de course, le temps,
            la description et l'allure moyenne, puis les totaux et leur variation par rapport à la semaine
            précédente. Fichier CSV à importer dans Google Sheets (Fichier &gt; Importer), un an au plus.
          </Typography>
          {erreurExport && <Alert severity="error" sx={{ mb: 2 }}>{erreurExport}</Alert>}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pt: 1 }}>
            <TextField
              label="Du"
              type="date"
              size="small"
              value={export_?.debut ?? ''}
              onChange={(ev) => export_ && setExport({ ...export_, debut: ev.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Au"
              type="date"
              size="small"
              value={export_?.fin ?? ''}
              onChange={(ev) => export_ && setExport({ ...export_, fin: ev.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExport(null)} disabled={exportEnCours}>Annuler</Button>
          <Button
            variant="contained"
            startIcon={<ExportIcon />}
            onClick={exporter}
            disabled={exportEnCours || !export_?.debut || !export_?.fin || export_.fin < export_.debut}
          >
            Exporter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
